import React, { useEffect, useId, useState } from "react";
import { Star, Send } from "lucide-react";
import {
  createConsultationReview,
  createGeneralReview,
  getSessionReview,
} from "../../Services/ConsultationReviewService";
import ReviewDetails from "./ReviewDetails";
import { formatReviewDate, isCompletedConsultation } from "./reviewHelpers";
import "./Reviews.css";

const ConsultationReviewForm = ({
  session,
  onSubmitted,
  draft,
  onDraftChange,
}) => {
  const id = useId();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [rating, setRating] = useState(draft?.rating || 0);
  const [content, setContent] = useState(draft?.content || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const sessionId = session?.id;
  const general = !session;
  const completed = general || isCompletedConsultation(session);

  useEffect(() => {
    if (general) {
      setLoading(false);
      return;
    }
    if (!completed) return;
    let active = true;
    setLoading(true);
    setLoadError(false);
    getSessionReview(sessionId)
      .then((data) => {
        if (active) setReview(data);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sessionId, general, completed, attempt]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const text = content.trim();
    if (!rating || !text || text.length > 1000) {
      setError("امتیاز و متن نظر (حداکثر ۱۰۰۰ نویسه) را وارد کنید.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await (general
        ? createGeneralReview({ rating, content: text })
        : createConsultationReview(sessionId, { rating, content: text }));
      setReview(saved);
      setSuccess(true);
      onSubmitted?.(saved);
    } catch (failure) {
      if (!general && failure.status === 409) {
        try {
          const existing = await getSessionReview(sessionId);
          if (existing) {
            setReview(existing);
            onSubmitted?.(existing);
          } else {
            setError(
              "وضعیت مشاوره تغییر کرده است؛ فقط پس از پایان مشاوره می‌توانید نظر بدهید. فهرست مشاوره‌ها را تازه‌سازی کنید.",
            );
          }
        } catch {
          setError(
            "وضعیت نظر قابل دریافت نیست. صفحه را تازه‌سازی کنید و دوباره تلاش کنید.",
          );
        }
      } else {
        setError(
          failure.status === 401
            ? "برای ثبت نظر دوباره وارد حساب کاربری شوید."
            : !general && (failure.status === 404 || failure.status === 400)
              ? "این مشاوره برای ثبت نظر در دسترس نیست. فهرست مشاوره‌ها را تازه‌سازی کنید."
              : "نظر ارسال نشد. متن شما حفظ شده؛ دوباره تلاش کنید.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!completed) return null;
  if (loading)
    return (
      <p className="review-muted" role="status">
        در حال دریافت وضعیت نظر…
      </p>
    );
  if (loadError)
    return (
      <div className="review-feedback" role="alert">
        دریافت وضعیت نظر ممکن نشد.{" "}
        <button
          type="button"
          className="review-button review-button--quiet"
          onClick={() => setAttempt((value) => value + 1)}
        >
          تلاش دوباره
        </button>
      </div>
    );
  if (review)
    return (
      <div>
        {success && (
          <p className="review-success" role="status">
            نظر شما ثبت شد و پس از تأیید پزشک یا ادمین نمایش داده می‌شود.
          </p>
        )}
        <ReviewDetails review={review} />
      </div>
    );

  return (
    <form
      className="review-form iran-sans"
      dir="rtl"
      onSubmit={submit}
      aria-labelledby={`${id}-title`}
    >
      <h3 id={`${id}-title`}>
        {general ? "نظر شما" : "تجربه مشاوره‌تان چطور بود؟"}
      </h3>
      <p className="review-muted">
        {general
          ? "درباره پزشک یا تجربه استفاده از خدمات بنویسید."
          : `دکتر ${session.doctorName} · ${formatReviewDate(session.endTime || session.startTime)}`}
      </p>
      <fieldset disabled={saving} className="review-score-picker">
        <legend>
          {general ? "امتیاز شما" : "امتیاز شما به پزشک و مشاوره"}
        </legend>
        <div className="review-stars">
          {[1, 2, 3, 4, 5].map((value) => (
            <label key={value} className={value <= rating ? "is-selected" : ""}>
              <input
                type="radio"
                name={`${id}-rating`}
                value={value}
                checked={rating === value}
                onChange={() => {
                  setRating(value);
                  onDraftChange?.({ rating: value, content });
                }}
                required
              />
              <Star
                size={27}
                aria-hidden="true"
                fill={value <= rating ? "currentColor" : "none"}
              />
              <span className="visually-hidden">{value} از ۵</span>
            </label>
          ))}
        </div>
        <span className="review-muted" aria-live="polite">
          {rating
            ? `${rating.toLocaleString("fa-IR")} از ۵`
            : "یک امتیاز انتخاب کنید"}
        </span>
      </fieldset>
      <label className="review-label" htmlFor={`${id}-content`}>
        متن نظر
      </label>
      <textarea
        id={`${id}-content`}
        rows={4}
        maxLength={1000}
        required
        disabled={saving}
        value={content}
        onChange={(event) => {
          setContent(event.target.value);
          onDraftChange?.({ rating, content: event.target.value });
        }}
        aria-describedby={`${id}-notice ${id}-count`}
      />
      <div id={`${id}-count`} className="review-count">
        <bdi dir="ltr">{content.length.toLocaleString("fa-IR")} / ۱۰۰۰</bdi>{" "}
        نویسه
      </div>

      {error && (
        <p className="review-feedback" role="alert">
          {error}
        </p>
      )}
      <button
        className="review-button"
        type="submit"
        disabled={saving || !rating || !content.trim()}
      >
        <Send size={17} aria-hidden="true" />
        {saving ? "در حال ثبت نظر…" : "ثبت و ارسال برای بررسی"}
      </button>
    </form>
  );
};

export default ConsultationReviewForm;
